<template>
  <div class="loyalty-card" :class="[loyaltyState, loyaltyData?.tierInfo?.tierName, fullWidth ? 'full-width-card' : 'carousel-card']">
    <div class="grid-x">
      <div class="grid-x cell margin-bottom-xs">
        <img :src="starRewardsLogoPlatinum" class="starIcon" />
        <span class="heavy-sm tier-name" data-fs-mask>{{ loyaltyData?.tierInfo?.tierName }}</span>
      </div>
      <section class="cell" :class="getGridClass">
        <div id="loyaltyHeaderZor" class="cell large-12">
          <h4 v-if="loyaltyData.staticData?.header" class="margin-bottom-xxs">
            {{ loyaltyData.staticData?.header }}
          </h4>
        </div>
        <div id="userMessageZor" class="cell large-12">
          <div v-if="loyaltyData.oneClickBronzeEnrollmentSuccess" class="successWrapper">
            <span class="confirmation-green-large success-icon"></span>
            <div>
              <span class="message body">{{ loyaltyData.staticData?.enrollSuccessMessage }}</span>
              <a :href="loyaltyData.staticData?.viewBenefitsUrl" class="secondary-cta">{{ loyaltyData.staticData?.viewBenefitsUrlText }}</a>
            </div>
          </div>
          <div v-else>
            <p class="message body" :class="{ 'margin-bottom-s': loyaltyData.staticData?.button && !fullWidth }">
              {{ loyaltyData.staticData?.message }}
              <a class="exclusionsLink link-body" :href="loyaltyData.staticData?.exclusionsUrl">{{ loyaltyData.staticData?.exclusionsLink }}</a>
            </p>
            <a
              v-if="loyaltyData.staticData?.link"
              id="loyaltyLink"
              :href="loyaltyData.staticData.url"
              :data-link="loyaltyData.staticData.linkValue"
              class="secondary-cta"
              @click.stop="handleLoyaltyLinkClickTag()"
            >
              {{ loyaltyData.staticData.link }}</a
            >
          </div>
        </div>
      </section>
      <section v-if="loyaltyState === LOYALTY_STATES.HAVE_STAR_MONEY" class="cell small-4 text-right">
        <p class="rewardMoney" data-fs-mask>
          <span class="h4">$</span><span class="h2">{{ loyaltyData?.rewardsInfo?.totalStarRewardCardsBalance }}</span>
        </p>
        <p class="pointsMessage body medium">
          {{ loyaltyData.staticData.pointsMessage }}
        </p>
      </section>
      <section v-if="loyaltyState === LOYALTY_STATES.NO_STAR_MONEY" class="cell medium-5 small-12" :class="{ 'large-12': !fullWidth }">
        <div class="tracker">
          <ProgressBar :value="getPointsToPercentage" data-fs-mask />
          <p class="progressBarIcon" :class="loyaltyData?.iconClass"></p>
          <p class="reward" data-fs-mask><sup class="h5">$</sup><span class="h2">10</span></p>
        </div>
        <div class="progress-bar-values">
          <div class="start-value" data-fs-mask>
            <b>{{ loyaltyData?.rewardsInfo?.currentPoints }}</b> / 1000 Points
          </div>
          <div class="end-value">Star Money</div>
        </div>
      </section>
      <section
        v-if="!loyaltyData.oneClickBronzeEnrollmentSuccess && loyaltyData.staticData?.button"
        class="cell medium-4 small-12 align-self-middle"
        :class="[fullWidth ? 'text-right' : 'large-12']"
      >
        <a
          id="signInLink"
          :href="loyaltyData.staticData?.url"
          :data-link="loyaltyData.staticData.linkValue"
          class="primary button button-small"
          @click.stop="loyaltyState === LOYALTY_STATES.ONE_CLICK_LOYALIST_ENROLL ? handleOneClickEnroll() : handleSignupClickTag()"
        >
          {{ loyaltyData.staticData.button }}</a
        >
      </section>
    </div>
  </div>
</template>

<script>
import ProgressBar from '@atomic-ui/progressBar/ProgressBar';
import starRewardsLogoPlatinum from '@core/csg/src/assets/images/mcom/compressed/star-rewards-platinum-large.png';
import { Cookie, UserStatus } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import { TIER_NAMES, TIER_ICONS, TIER_HRESHOLD, LOYALTY_STATES } from './helpers/constants';
import oneClickEnroll from './helpers/oneClickEnroll';
import data from './helpers/loyaltyStaticData.json';

export default {
  name: 'LoyaltyCard',
  components: {
    ProgressBar,
  },
  props: {
    fullWidth: {
      type: Boolean,
      default: () => false,
    },
  },
  setup() {
    return {
      starRewardsLogoPlatinum,
      LOYALTY_STATES,
    };
  },
  data() {
    return {
      loyaltyState: TIER_NAMES.GUEST,
      loyaltyData: {},
    };
  },
  computed: {
    getGridClass() {
      let gridClass = 'large-12 ';
      if (this.fullWidth) {
        gridClass = 'padding-right-s ';
      }
      if (this.loyaltyState === LOYALTY_STATES.HAVE_STAR_MONEY) {
        return 'small-8';
      }
      if (this.loyaltyState === LOYALTY_STATES.NO_STAR_MONEY) {
        return `${gridClass}medium-7 small-12`;
      }
      if (
        this.loyaltyState === LOYALTY_STATES.GUEST ||
        this.loyaltyState === LOYALTY_STATES.NON_LOYALIST ||
        this.loyaltyState === LOYALTY_STATES.ONE_CLICK_LOYALIST_ENROLL
      ) {
        return `${gridClass}medium-8 small-12`;
      }
      return 'small-12';
    },
    getPointsToPercentage() {
      const points = this.currentPoints();
      return (points * 100) / 1000;
    },
  },
  mounted() {
    if (this.isSignedIn()) {
      this.loyaltyData = this.getStarRewardsInfo();
      if (this.tierName() === TIER_NAMES.DEFAULT) {
        if (this.isOneClickEnrollEnabled()) {
          this.enableNonLoyaltyOneClickBanner();
        } else {
          this.enableNonLoyaltyBanner();
        }
      } else {
        this.enableLoyaltyBanner();
      }
    } else {
      this.loyaltyData.staticData = data[LOYALTY_STATES.GUEST];
    }
  },
  methods: {
    isSignedIn() {
      return UserStatus.isSoftSignedIn() || UserStatus.isSignedIn();
    },
    getStarRewardsInfo() {
      const uid = Cookie.get('macys_online_uid');
      const info = uid ? localStorage.getItem(`hnfsr_${uid}`) : '';
      let starRewardsInfo = {};
      if (info) {
        try {
          starRewardsInfo = JSON.parse(info);
          /* eslint-disable @typescript-eslint/no-unused-vars */
        } catch (e) {
          starRewardsInfo = {};
        }
        /* eslint-enable @typescript-eslint/no-unused-vars */
      }
      return starRewardsInfo;
    },
    isOneClickEnrollEnabled() {
      return this.loyaltyData?.starRewardsAutoEnrollEnabled;
    },
    enableLoyaltyBanner() {
      this.mapStaticData();
    },
    enableNonLoyaltyOneClickBanner() {
      this.loyaltyData.staticData = data[TIER_NAMES.ONE_CLICK_LOYALIST_ENROLL];
      this.loyaltyState = LOYALTY_STATES.ONE_CLICK_LOYALIST_ENROLL;
    },
    enableNonLoyaltyBanner() {
      this.loyaltyData.staticData = data[TIER_NAMES.DEFAULT];
      this.loyaltyState = LOYALTY_STATES.NON_LOYALIST;
    },
    mapStaticData() {
      const tierName = this.tierName().toLowerCase();
      const currentPoints = this.currentPoints();
      const starMoneyBalance = this.starMoneyBalance();
      const yearToDateSpend = this.yearToDateSpend();
      const isCloseToReward = this.isCloseToReward(tierName, currentPoints);
      const iconClass = TIER_ICONS[tierName];

      let staticData;
      if (starMoneyBalance !== 0) {
        staticData = data.signedInHasStarMoney;
      } else if (currentPoints === 0) {
        const spendForNextUpgrade = this.spendForNextUpgrade(tierName, yearToDateSpend);
        const unlockLevelName = this.unlockNextLevel(tierName, yearToDateSpend);
        if (unlockLevelName === TIER_NAMES.PLATINUM) {
          staticData = data.unlockNextLevel[tierName];
          staticData.message = `Platinum members earn points faster when shopping at Macy’s. Reach Platinum when you spend $${spendForNextUpgrade} more at Macy’s with your Macy’s Card by 12/31.`;
        } else if (unlockLevelName === TIER_NAMES.GOLD) {
          staticData = data.unlockNextLevel[tierName];
          staticData.message = `Gold members earn points faster when shopping at Macy’s. Reach Gold when you spend $${spendForNextUpgrade} more at Macy’s with your Macy’s Card by 12/31.`;
        } else {
          staticData = data.signedInNoPointsNoStarMoney[tierName];
        }
      } else if (isCloseToReward) {
        const spendForNextReward = this.spendForNextReward(tierName, currentPoints);
        staticData =
          tierName === TIER_NAMES.BRONZE
            ? data.signedInHasPointsNoStarMoney.closeToReward[tierName]
            : data.signedInHasPointsNoStarMoney.closeToReward.nonBronze;
        staticData.message =
          tierName === TIER_NAMES.BRONZE
            ? `Spend $${spendForNextReward} on Macy’s merchandise to unlock your next $10 Star Money reward.`
            : `Spend $${spendForNextReward} on Macy’s merchandise with your Macy’s Card to unlock your next reward.`;
      } else {
        staticData = data.signedInHasPointsNoStarMoney.notCloseToReward[tierName];
      }

      // set the staticdata and userName along with loyalty details
      this.loyaltyData.rewardsInfo.totalStarRewardCardsBalance = starMoneyBalance;
      this.loyaltyData.tierInfo.tierName = tierName;
      this.loyaltyData.userName = this.userName();
      this.loyaltyData.iconClass = iconClass;
      this.loyaltyData.staticData = staticData;
      this.loyaltyState = staticData.state;
    },
    userName() {
      return Cookie.get('UserName', 'MISCGCs') || '';
    },
    tierName() {
      const tierInfo = this.loyaltyData?.tierInfo;
      if (!tierInfo) return TIER_NAMES.DEFAULT;
      return tierInfo?.tierName || TIER_NAMES.DEFAULT;
    },
    currentPoints() {
      return this.loyaltyData?.rewardsInfo?.currentPoints || 0;
    },
    starMoneyBalance() {
      const srBalance = this.loyaltyData?.rewardsInfo?.totalStarRewardCardsBalance || 0;
      return parseInt(srBalance, 10);
    },
    yearToDateSpend() {
      const yearToDateSpendVal = this.loyaltyData?.tierInfo?.yearToDateSpend || 0;
      return parseInt(yearToDateSpendVal, 10);
    },
    isCloseToReward(tierName, currentPoints) {
      const isClosetoReward =
        (currentPoints >= 500 && tierName === TIER_NAMES.PLATINUM) ||
        (currentPoints >= 750 && tierName === TIER_NAMES.GOLD) ||
        (currentPoints >= 900 && (tierName === TIER_NAMES.SILVER || tierName === TIER_NAMES.BRONZE));
      return isClosetoReward;
    },
    pointsThresholdForAward() {
      return this.loyaltyData?.rewardsInfo?.pointsThresholdForAward || 1000;
    },
    unlockNextLevel(tierName, yearToDateSpend) {
      let unlockLevelName;
      if (tierName === TIER_NAMES.GOLD && yearToDateSpend >= TIER_HRESHOLD.PLATINUM - 200) {
        unlockLevelName = TIER_NAMES.PLATINUM;
      } else if (tierName === TIER_NAMES.SILVER && yearToDateSpend >= TIER_HRESHOLD.GOLD - 100) {
        unlockLevelName = TIER_NAMES.GOLD;
      }
      return unlockLevelName;
    },
    spendForNextUpgrade(tierName, spendAmount) {
      const nextThreshold = tierName === TIER_NAMES.SILVER ? TIER_HRESHOLD.GOLD : TIER_HRESHOLD.PLATINUM;
      const spendForNextUpgrade = (nextThreshold - spendAmount).toFixed(2);
      return parseInt(spendForNextUpgrade, 10);
    },
    spendForNextReward(tierName, currentPoints) {
      const PointsforNextReward = data.PointsforNextReward[tierName];
      const spendForNextReward = (this.pointsThresholdForAward() - currentPoints) * (10 / PointsforNextReward);
      return parseInt(spendForNextReward, 10);
    },
    handleLoyaltyLinkClickTag() {
      const linkName = this.loyaltyData.staticData.linkValue;
      fireTag('link', { link_name: linkName, event_name: 'hp dash click' });
    },
    handleSignupClickTag() {
      fireTag('link', { link_name: 'hp_dash-loyalty signup', event_name: 'hp dash click' });
    },
    async handleOneClickEnroll() {
      try {
        await oneClickEnroll();
        this.loyaltyData.oneClickBronzeEnrollmentSuccess = true;
        /* eslint-disable @typescript-eslint/no-unused-vars */
      } catch (error) {
        window.location.href = '/loyalty/starrewards';
      }
      /* eslint-enable @typescript-eslint/no-unused-vars */
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_star-rewards-bronze-large.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_star-rewards-silver-large.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_star-rewards-gold-large.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_star-rewards-platinum-large.scss';
.cell {
  & > .loyalty-card {
    width: calc(200% + 24px);
    height: 100%;
    margin-bottom: 0;
    justify-content: start;
  }
}

.loyalty-card {
  width: 100%;
  margin-bottom: rem-calc(32px);
  background-color: $background-color;
  border-top: rem-calc(6px) solid $primary-color;
  display: flex;
  flex-direction: column;
  gap: rem-calc(16px);

  ::v-deep(.p-progressbar) {
    border: none;
  }

  .exclusionsLink {
    color: $gray-1-color;
  }
}

.starIcon {
  margin-top: 0;
  height: rem-calc(14px);
  width: rem-calc(114px);
}

.tier-name {
  text-transform: capitalize !important;
  margin-left: rem-calc(6px);
}

.bronze {
  background-color: $loyalty-bg-1-color;
  border-top: rem-calc(6px) solid $loyalty-1-color;

  ::v-deep(.progress-bar) {
    .p-progressbar-value {
      background-color: $loyalty-1-color;
    }

    .progress-dot {
      border-color: $loyalty-1-color;
    }
  }
}

.silver {
  background-color: $loyalty-bg-2-color;
  border-top: rem-calc(6px) solid $loyalty-2-color;

  ::v-deep(.progress-bar) {
    .p-progressbar-value {
      background-color: $loyalty-2-color;
    }

    .progress-dot {
      border-color: $loyalty-2-color;
    }
  }
}

.gold {
  background-color: $loyalty-bg-3-color;
  border-top: rem-calc(6px) solid $loyalty-3-color;

  ::v-deep(.progress-bar) {
    .p-progressbar-value {
      background-color: $loyalty-3-color;
    }

    .progress-dot {
      border-color: $loyalty-3-color;
    }
  }
}

.platinum {
  background-color: $loyalty-bg-4-color;
  border-top: rem-calc(6px) solid $loyalty-4-color;

  ::v-deep(.progress-bar) {
    .p-progressbar-value {
      background-color: $loyalty-4-color;
    }

    .progress-dot {
      border-color: $loyalty-4-color;
    }
  }
}

.tracker {
  display: flex;
  align-items: center;
  width: 100%;

  .progress-bar {
    width: inherit;
  }

  .progressBarIcon {
    width: rem-calc(22px);
    height: rem-calc(22px);
    position: relative;
    background-size: 100%;
    right: rem-calc(6px);
    float: right;
  }
}

.progress-bar-values {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  .end-value {
    white-space: nowrap;
  }
}

.carousel-card {
  padding: rem-calc(14 16 20 16);
  &:not(.HaveStarMoney) section {
    width: 100%;
  }
  @include breakpoint(medium) {
    padding: rem-calc(42 20 20 20);
  }
  @include breakpoint(large) {
    padding-top: 20%;
  }
}

.full-width-card {
  padding: rem-calc(14 16 20 16);
  @include breakpoint(medium) {
    padding: rem-calc(14 62 20 62);
  }
}

#userMessageZor {
  .message {
    margin-bottom: rem-calc(24px);
    @include breakpoint(large) {
      margin-bottom: rem-calc(16px);
    }
  }
}
.HaveStarMoney {
  #userMessageZor {
    .message {
      margin-bottom: rem-calc(16px);
    }
  }
}
.guest,
.nonLoyalist {
  &.full-width-card {
    #userMessageZor {
      .message {
        margin-bottom: rem-calc(0px);
      }
    }
  }
}
.NoStarMoney.carousel-card {
  #userMessageZor {
    margin-bottom: rem-calc(8px);
    @include breakpoint(medium) {
      margin-bottom: rem-calc(16px);
    }
  }
}
.rewardMoney {
  display: flex;
  justify-content: flex-end;
  .h4 {
    position: relative;
    top: rem-calc(4px);
  }
}
</style>
